/**
 * Tracks the Fuego app banner injected at the top of the viewport and
 * exposes its current height as the `--fuego-banner-height` CSS variable
 * on :root. CSS (e.g. the mobile menu drawer) can then account for it
 * so that fixed-position elements stay fully within the visible area.
 */
;(function () {
  const BANNER_SELECTOR =
    '#fuego-app-banner, .fuego-app-banner, [id^="fuego-app-banner"], [class*="fuego-app-banner"]'
  const CSS_VAR = '--fuego-banner-height'

  let currentBanner = null
  let resizeObserver = null
  let rafId = null
  let rafLayoutFixId = null

  const setHeight = (pxValue) => {
    const value = `${Math.max(0, Math.round(pxValue))}px`
    if (document.documentElement.style.getPropertyValue(CSS_VAR) !== value) {
      document.documentElement.style.setProperty(CSS_VAR, value)
    }
  }

  // Match theme.liquid (layout/theme.liquid) so drawer height and --vh stay in sync
  const syncVhFromWindow = () => {
    const vh = window.innerHeight * 0.01
    document.documentElement.style.setProperty('--vh', vh + 'px')
  }

  /**
   * iOS: when --fuego-banner-height or --vh changes, flex + overflow-y areas may not
   * reflow. Nudging scrollTop and touching layout forces a recalculation.
   */
  const nudgeOpenMenuDrawer = () => {
    const drawer = document.getElementById('menu-drawer') || document.querySelector('.menu-drawer')
    if (!drawer) return
    const details = drawer.closest('details')
    if (!details || !details.hasAttribute('open')) return
    void drawer.offsetHeight
    const main = drawer.querySelector('.menu-drawer__main')
    if (!main) return
    const st = main.scrollTop
    main.scrollTop = st ? st - 1 : 1
    main.scrollTop = st
  }

  const queueLayoutFix = () => {
    if (rafLayoutFixId) cancelAnimationFrame(rafLayoutFixId)
    rafLayoutFixId = requestAnimationFrame(() => {
      rafLayoutFixId = null
      syncVhFromWindow()
      requestAnimationFrame(function () {
        nudgeOpenMenuDrawer()
      })
    })
  }

  /** Pixels of the element that actually intersect the vertical viewport. */
  const visibleHeightInViewport = (rect) => {
    const h = window.innerHeight
    return Math.max(0, Math.min(rect.bottom, h) - Math.max(0, rect.top))
  }

  const measure = (el) => {
    if (!el || !el.isConnected) {
      setHeight(0)
      queueLayoutFix()
      return
    }

    const style = window.getComputedStyle(el)
    if (style.display === 'none' || style.visibility === 'hidden') {
      setHeight(0)
      queueLayoutFix()
      return
    }
    if (el.hasAttribute('hidden') || el.getAttribute('aria-hidden') === 'true') {
      setHeight(0)
      queueLayoutFix()
      return
    }
    const opacity = parseFloat(style.opacity)
    if (!isNaN(opacity) && opacity < 0.01) {
      setHeight(0)
      queueLayoutFix()
      return
    }

    const rect = el.getBoundingClientRect()
    // Dismissal often uses transform (banner still has box height, but is off-screen)
    setHeight(visibleHeightInViewport(rect))
    queueLayoutFix()
  }

  const scheduleMeasure = (el) => {
    if (rafId) cancelAnimationFrame(rafId)
    rafId = requestAnimationFrame(() => measure(el))
  }

  const findBanner = () => document.querySelector(BANNER_SELECTOR)

  const observeForInjection = () => {
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    })
  }

  const attach = (el) => {
    if (currentBanner === el) return
    if (resizeObserver) {
      resizeObserver.disconnect()
      resizeObserver = null
    }
    mutationObserver.disconnect()
    currentBanner = el
    if (!el) {
      setHeight(0)
      queueLayoutFix()
      observeForInjection()
      return
    }
    mutationObserver.observe(el, {
      attributes: true,
      attributeFilter: ['style', 'class', 'hidden', 'aria-hidden'],
    })
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => scheduleMeasure(el))
      resizeObserver.observe(el)
    }
    scheduleMeasure(el)
  }

  const findBannerInRecords = (records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (!node || node.nodeType !== 1) continue
        if (node.matches && node.matches(BANNER_SELECTOR)) return node
        if (node.querySelector) {
          const nested = node.querySelector(BANNER_SELECTOR)
          if (nested) return nested
        }
      }
    }
    return null
  }

  const sync = (records) => {
    if (!currentBanner) {
      const found = records && records.length ? findBannerInRecords(records) : findBanner()
      if (found) attach(found)
      return
    }
    if (!currentBanner.isConnected) {
      attach(null)
      return
    }
    scheduleMeasure(currentBanner)
  }

  const mutationObserver = new MutationObserver(sync)

  const onViewportChange = () => {
    syncVhFromWindow()
    scheduleMeasure(currentBanner)
  }

  /** Banner may be `position: sticky` and leave the viewport on scroll; height must update (scroll is not covered by ResizeObserver). */
  const onScroll = () => {
    if (!currentBanner) return
    scheduleMeasure(currentBanner)
  }

  const init = () => {
    const initial = findBanner()
    if (initial) attach(initial)
    else observeForInjection()
    window.addEventListener('resize', onViewportChange, { passive: true })
    window.addEventListener('orientationchange', onViewportChange, { passive: true })
    window.addEventListener('scroll', onScroll, { passive: true, capture: true })
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', onViewportChange, { passive: true })
      window.visualViewport.addEventListener('scroll', onScroll, { passive: true })
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true })
  } else {
    init()
  }
})()
